import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';


function installIosConsoleDiagnostics() {
  try {
    if (typeof window === 'undefined' || window.__fzIosConsoleDiagnosticsInstalled) return;
    window.__fzIosConsoleDiagnosticsInstalled = true;

    const isNative = !!window.Capacitor?.isNativePlatform?.();
    const recent = new Map();
    const history = [];
    const HISTORY_LIMIT = 25;

    const onceKey = (key, ttl = 3000) => {
      const now = Date.now();
      const last = recent.get(key) || 0;
      if (now - last < ttl) return false;
      recent.set(key, now);
      return true;
    };

    const safeStringify = (value, depth = 0) => {
      try {
        if (value == null) return String(value);
        if (typeof value === 'string') return value;
        if (value instanceof Error) return `${value.name || 'Error'}: ${value.message || ''}${value.stack ? `\n${value.stack}` : ''}`;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (depth > 1) return Object.prototype.toString.call(value);
        const seen = new WeakSet();
        return JSON.stringify(value, (key, val) => {
          if (typeof val === 'function') return `[Function ${val.name || 'anonymous'}]`;
          if (val instanceof Error) return { name: val.name, message: val.message, stack: val.stack };
          if (typeof val === 'object' && val !== null) {
            if (seen.has(val)) return '[Circular]';
            seen.add(val);
          }
          return val;
        });
      } catch (_) {
        try { return String(value); } catch (__) { return '[unserializable]'; }
      }
    };

    const readContext = () => {
      try {
        return {
          href: window.location?.href || '',
          hash: window.location?.hash || '',
          appVersion: window.APP_VERSION || '',
          lastModule: window.__fzLastOpenedModule || '',
          lastPrioritySync: window.__fzLastPrioritySync || null,
          lastBgUpdate: window.__fzLastBgUpdate || null,
          lastLiveDeepLink: window.__fzLastLiveDeepLink || null,
          visibility: document.visibilityState || '',
          userAgent: navigator.userAgent || '',
        };
      } catch (_) {
        return {};
      }
    };

    const storeDiagnostic = (entry) => {
      try {
        const enriched = { ...entry, at: new Date().toISOString(), context: readContext() };
        history.push(enriched);
        while (history.length > HISTORY_LIMIT) history.shift();
        window.__fzLastJsError = enriched;
        window.__fzLastJsErrors = history.slice();
        try { sessionStorage.setItem('fz:lastJsError', JSON.stringify(enriched)); } catch (_) {}
        try { sessionStorage.setItem('fz:lastJsErrors', JSON.stringify(history)); } catch (_) {}
        return enriched;
      } catch (_) {
        return entry;
      }
    };

    const formatDiagnostic = (title, entry) => {
      const parts = [
        `[Trembo JS DIAGNOSTIC] ${title}`,
        `message: ${entry.message || ''}`,
      ];
      if (entry.file || entry.line || entry.col) parts.push(`source: ${entry.file || ''}:${entry.line || 0}:${entry.col || 0}`);
      if (entry.stack) parts.push(`stack:\n${entry.stack}`);
      if (entry.args) parts.push(`args: ${entry.args}`);
      try { parts.push(`context: ${JSON.stringify(entry.context || readContext())}`); } catch (_) {}
      return parts.join('\n');
    };

    const originalConsoleError = console.error ? console.error.bind(console) : null;
    const originalConsoleWarn = console.warn ? console.warn.bind(console) : null;
    const originalConsoleInfo = console.info ? console.info.bind(console) : null;

    const emitDiagnostic = (title, entry, keyPrefix = title, ttl = 60000) => {
      const saved = storeDiagnostic(entry);
      const key = `${keyPrefix}:${saved.message || saved.args || ''}:${saved.file || ''}:${saved.line || ''}:${saved.col || ''}`.slice(0, 500);
      if (!onceKey(key, isNative ? ttl : 5000)) return;
      const formatted = formatDiagnostic(title, saved);
      // No iOS/Capacitor, console.error vira uma linha minificada e pode entrar em loop.
      // Registramos a ocorrência completa como warn/info e guardamos em sessionStorage,
      // sem chamar console.error nativo. No web, preservamos console.error original.
      if (isNative) {
        (originalConsoleWarn || originalConsoleInfo || (() => {}))(formatted);
      } else if (originalConsoleError) {
        originalConsoleError(formatted);
      }
    };

    console.error = (...args) => {
      try {
        const firstError = args.find((arg) => arg instanceof Error || arg?.stack || arg?.message);
        const entry = {
          type: 'console.error',
          message: firstError?.message || args.map((arg) => safeStringify(arg)).join(' '),
          stack: firstError?.stack || new Error('console.error stack').stack || '',
          args: args.map((arg) => safeStringify(arg)).join(' | '),
        };
        emitDiagnostic('console.error capturado', entry, 'console.error', 60000);
        if (!isNative && originalConsoleError) originalConsoleError(...args);
      } catch (_) {
        if (!isNative && originalConsoleError) originalConsoleError(...args);
      }
    };

    window.addEventListener('error', (event) => {
      try {
        const target = event?.target;
        if (target && target !== window) {
          const tag = String(target?.tagName || '').toLowerCase();
          const src = target?.currentSrc || target?.src || target?.href || '';
          if (tag === 'img' || tag === 'source' || tag === 'link' || tag === 'script') {
            const entry = { type: 'asset', message: `Falha ao carregar/decodificar ${tag}`, file: src, line: 0, col: 0, stack: '' };
            emitDiagnostic('asset error', entry, `asset:${src || tag}`, 120000);
            if (isNative) event.preventDefault?.();
            return true;
          }
          return;
        }
        const err = event?.error;
        const message = event?.message || err?.message || safeStringify(err) || 'Erro JS sem mensagem';
        const entry = {
          type: 'window.error',
          message,
          file: event?.filename || '',
          line: event?.lineno || 0,
          col: event?.colno || 0,
          stack: err?.stack || '',
        };
        emitDiagnostic('window.error', entry, 'window.error', 60000);
        if (isNative) event.preventDefault?.();
      } catch (_) {}
    }, true);

    window.addEventListener('unhandledrejection', (event) => {
      try {
        const reason = event?.reason;
        const entry = {
          type: 'unhandledrejection',
          message: reason?.message || safeStringify(reason) || 'Promise rejeitada sem mensagem',
          stack: reason?.stack || '',
          args: safeStringify(reason),
        };
        emitDiagnostic('unhandledrejection', entry, 'promise', 60000);
        if (isNative) event.preventDefault?.();
      } catch (_) {}
    });

    window.__fzDumpLastJsErrors = () => {
      try {
        const payload = JSON.stringify(window.__fzLastJsErrors || [], null, 2);
        (originalConsoleInfo || originalConsoleWarn || (() => {}))(`[Trembo JS DIAGNOSTIC DUMP]\n${payload}`);
        return window.__fzLastJsErrors || [];
      } catch (_) {
        return [];
      }
    };
  } catch (_) {}
}

installIosConsoleDiagnostics();

const rootEl = document.getElementById('root');

try {
  if (typeof window !== 'undefined') {
    window.__fzReactBootstrapStarted = true;
    window.__fzReactBootstrapStartedAt = Date.now();
  }
} catch (e) {}

const root = ReactDOM.createRoot(rootEl);
root.render(<App />);

try {
  if (typeof window !== 'undefined') {
    window.__fzAppMounted = true;
    window.dispatchEvent(new Event('fz-app-mounted'));
    window.__fzLastAppMountAt = Date.now();
    const hide = () => {
      if (typeof window.__fzHideBootSplash === 'function') {
        requestAnimationFrame(() => {
          setTimeout(() => window.__fzHideBootSplash(), 120);
        });
      }
    };
    hide();
    window.addEventListener('pageshow', (event) => {
      if (event && event.persisted) {
        setTimeout(hide, 80);
        return;
      }
      setTimeout(hide, 40);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') setTimeout(hide, 40);
    });
  }
} catch (e) {}
